/**
 * "Compartilhar contato" (#595): escolher um contato cadastrado e pôr o
 * cartão dele no campo do editor.
 *
 * ── Duas leituras, de propósito ───────────────────────────────────────────
 *
 * A busca usa a LISTA de clientes: a mesma carteira, a mesma busca por nome,
 * telefone e e-mail, e o telefone MASCARADO. Ela serve para escolher. O
 * telefone inteiro vem depois, de uma rota que lê UM contato, o escolhido, e
 * deixa registrado quem leu o telefone de quem. Trazer telefones
 * inteiros na lista para montar a mensagem seria expor em volume o que só um
 * precisa.
 *
 * ── Entra no campo, não sai direto ────────────────────────────────────────
 *
 * Como as mensagens prontas da organização: o texto entra no campo, a pessoa
 * confere e aperta Enviar. Mandar direto era o caminho para o contato errado
 * chegar ao cliente errado sem ninguém ter lido.
 *
 * ── Contato sem telefone fica na lista, travado ───────────────────────────
 *
 * Sumir com ele faria quem o procura concluir que a busca falhou. Ele aparece,
 * não pode ser escolhido, e diz por quê.
 */

import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { ClienteDaLista } from '@cliqcrm/contracts';
import { Botao, Campo, Dialogo } from '@/shared/ui';
import { useApi } from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { useListaDeClientes } from '@/entities/clientes/cliente';
import { mensagemDeContato } from '../lib/cartao-de-contato';

const SEM_TELEFONE = 'Sem telefone cadastrado';

export function DialogoDeCompartilharContato({
  aoUsar,
  aoFechar,
}: {
  /** Põe o texto no campo do editor. A pessoa confere e manda. */
  aoUsar: (texto: string) => void;
  aoFechar: () => void;
}): ReactNode {
  const api = useApi();
  const [busca, definirBusca] = useState('');
  const [lendo, definirLendo] = useState<string | null>(null);
  const [falha, definirFalha] = useState<string | null>(null);
  const campo = useRef<HTMLInputElement>(null);
  /*
   * Fechar o modal enquanto o telefone carrega é desistir. Sem esta marca, a
   * resposta que chegasse depois poria o contato no campo mesmo assim.
   */
  const aberto = useRef(true);
  useEffect(() => {
    aberto.current = true;
    return () => {
      aberto.current = false;
    };
  }, []);

  const lista = useListaDeClientes({
    busca,
    recorte: 'todos',
    ordenar: 'nome',
    direcao: 'asc',
  });

  async function escolher(contato: ClienteDaLista): Promise<void> {
    if (!api || !contato.telefone || lendo) return;
    definirLendo(contato.id);
    definirFalha(null);
    try {
      const cartao = await api.cartaoDeContato({ caminho: { id: contato.id } });
      if (!aberto.current) return;
      if (!cartao.telefone) {
        definirFalha(`${cartao.nome ?? 'Este contato'} não tem telefone cadastrado.`);
        return;
      }
      aoUsar(mensagemDeContato({ ...cartao, telefone: cartao.telefone }));
      aoFechar();
    } catch (erro) {
      if (!aberto.current) return;
      definirFalha(
        recadoDeErro(erro, {
          porCodigo: {
            NAO_ENCONTRADO: 'Este contato não está mais disponível para você.',
          },
        }),
      );
    } finally {
      if (aberto.current) definirLendo(null);
    }
  }

  /*
   * Enter na busca escolhe o primeiro que pode ser escolhido — mas só quando a
   * lista já responde ao que está digitado. A busca espera a pessoa parar de
   * digitar; um Enter rápido depois de "Pedro" pegaria o primeiro da lista
   * ANTERIOR, e o contato errado iria para o campo.
   */
  const listaEmDia = !lista.carregando && lista.termoDosItens === busca.trim();

  function aoTeclar(evento: KeyboardEvent<HTMLInputElement>): void {
    if (evento.key !== 'Enter') return;
    evento.preventDefault();
    if (!listaEmDia) return;
    const primeiro = lista.itens.find((contato) => contato.telefone);
    if (primeiro) void escolher(primeiro);
  }

  return (
    <Dialogo
      titulo="Compartilhar contato"
      aoFechar={aoFechar}
      botoes={<Botao onClick={aoFechar}>Fechar</Botao>}
    >
      <p className="dialogo__apoio">
        O contato entra no campo com o telefone e o link do WhatsApp. Você confere e envia.
      </p>

      <Campo
        ref={campo}
        rotulo="Buscar"
        type="search"
        value={busca}
        autoFocus
        placeholder="nome, telefone ou e-mail"
        onChange={(evento) => definirBusca(evento.target.value)}
        onKeyDown={aoTeclar}
      />

      {falha && (
        <p className="dialogo__erro" role="alert">
          {falha}
        </p>
      )}

      {lista.erro ? (
        /*
         * Falha da busca não é "ninguém cadastrado". Dizer que não há contatos
         * quando o servidor não respondeu faria a pessoa desistir de um contato
         * que existe.
         */
        <div className="dialogo__erro" role="alert">
          <p>{recadoDeErro(lista.erro, { generica: 'Não foi possível buscar os contatos.' })}</p>
          <Botao onClick={lista.recarregar}>Tentar de novo</Botao>
        </div>
      ) : lista.itens.length === 0 ? (
        <p className="dialogo__erro" role="status">
          {lista.carregando
            ? 'Buscando contatos…'
            : busca.trim() === ''
              ? 'Nenhum contato cadastrado que você possa ver.'
              : `Nenhum contato com "${busca.trim()}".`}
        </p>
      ) : (
        <ul className="lista-de-modelos">
          {lista.itens.map((contato) => (
            <li key={contato.id}>
              <button
                type="button"
                className="lista-de-modelos__item"
                disabled={!contato.telefone || lendo !== null}
                title={
                  contato.telefone ? 'Põe o contato no campo para você conferir' : SEM_TELEFONE
                }
                onClick={() => void escolher(contato)}
              >
                <span className="contato-compartilhado__nome">
                  {contato.nome_exibicao?.trim() || 'Sem nome'}
                </span>
                <span className="contato-compartilhado__detalhe">
                  {lendo === contato.id
                    ? 'Buscando o telefone…'
                    : (contato.telefone ?? SEM_TELEFONE)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Dialogo>
  );
}
